// D1: PDF de la nota (sin imágenes) + envío por correo al cliente.
// Lo usan ListadoPendienteSurtir (al surtir un pedido) y VentaDirectaCreate.
//
// Se mantiene aquí para que las dos vistas manden exactamente el mismo
// documento y el mismo cuerpo de correo.

import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { encode } from "base64-arraybuffer";
import axios from "axios";
import Swal from "sweetalert2";

const MAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
// Valores "placeholder" que el sistema guarda cuando no hay correo real
const PLACEHOLDERS = ["", "na", "a@a.a"];

export const correoValido = (email) => {
  const e = String(email || "").trim();
  if (PLACEHOLDERS.includes(e.toLowerCase())) return false;
  return MAIL_REGEX.test(e);
};

/**
 * Un cliente puede tener VARIOS correos en el mismo campo, separados por
 * coma, punto y coma o espacio. Devuelve sólo los válidos, sin repetidos.
 *   "a@b.com, c@d.com" → ["a@b.com", "c@d.com"]
 */
export const parseCorreos = (valor) =>
  String(valor || "")
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter((x) => correoValido(x))
    .filter((x, i, arr) => arr.findIndex((y) => y.toLowerCase() === x.toLowerCase()) === i);

const money = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

/**
 * Genera el PDF de la nota.
 * @param {object} p
 * @param {string|number} p.numero      idSurtido
 * @param {string} p.fecha
 * @param {string} p.cliente            nombre comercial
 * @param {string} [p.razonSocial]
 * @param {string} [p.observaciones]
 * @param {Array}  p.lineas             [{ codigo, nombre, cantidad, precio, descuento, total }]
 * @param {number} p.piezas
 * @param {number} p.subTotal
 * @param {number} p.iva
 * @param {number} p.total
 */
export function generaPDFNota(p) {
  const doc = new jsPDF();

  const img = new Image();
  img.src = process.env.PUBLIC_URL + "logoInstitucion.png";
  doc.addImage(img, "png", 12, 8, 33, 18);

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(`NOTA # ${p.numero || ""}`, 200, 16, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Fecha: ${p.fecha || ""}`, 200, 23, { align: "right" });

  doc.text(`Cliente: ${p.cliente || ""}`, 12, 34);
  let y = 39;
  if (p.razonSocial) {
    doc.text(`Razón social: ${p.razonSocial}`, 12, y);
    y += 5;
  }
  if (p.observaciones && p.observaciones !== "NA") {
    const obs = doc.splitTextToSize(`Observaciones: ${p.observaciones}`, 185);
    doc.text(obs, 12, y);
    y += obs.length * 5;
  }

  const hayDescuentos = (p.lineas || []).some((l) => Number(l.descuento) > 0);
  const head = hayDescuentos
    ? ["Artículo", "Cantidad", "Precio", "Desc %", "Importe"]
    : ["Artículo", "Cantidad", "Precio", "Importe"];

  const body = (p.lineas || []).map((l) => {
    const cols = [
      `${l.codigo || ""} ${l.nombre || ""}`.trim(),
      new Intl.NumberFormat("en-US").format(Number(l.cantidad) || 0),
      money(l.precio),
    ];
    if (hayDescuentos) {
      cols.push(Number(l.descuento) > 0 ? `${Number(l.descuento).toFixed(2)}%` : "-");
    }
    cols.push(money(l.total));
    return cols;
  });

  doc.autoTable({
    head: [head],
    body,
    startY: Math.max(y + 3, 50),
    theme: "grid",
    styles: { fontSize: 9 },
    headStyles: { fillColor: ["#B02307"], lineWidth: 0 },
    columnStyles: hayDescuentos
      ? { 1: { halign: "center" }, 2: { halign: "right" }, 3: { halign: "center" }, 4: { halign: "right" } }
      : { 1: { halign: "center" }, 2: { halign: "right" }, 3: { halign: "right" } },
    margin: { left: 12, right: 12 },
  });

  const yFin = doc.lastAutoTable.finalY + 8;
  doc.setFontSize(10);
  doc.text(`Piezas: ${new Intl.NumberFormat("en-US").format(Number(p.piezas) || 0)}`, 12, yFin);
  doc.text(`Subtotal: ${money(p.subTotal)}`, 200, yFin, { align: "right" });
  doc.text(`IVA: ${money(p.iva)}`, 200, yFin + 6, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(`TOTAL: ${money(p.total)}`, 200, yFin + 14, { align: "right" });

  return doc;
}

/**
 * Manda la nota en PDF automáticamente al CLIENTE y al VENDEDOR.
 * No pregunta nada: se envía en cuanto se guarda la venta.
 *
 * Los destinatarios sin correo válido simplemente se omiten; el envío nunca
 * interrumpe ni revierte la venta, que ya quedó guardada.
 *
 * @param {object} opts
 * @param {object} opts.datosPDF   lo que recibe generaPDFNota
 * @param {string} opts.emailCliente
 * @param {string} opts.nombreCliente
 * @param {string} opts.emailVendedor
 * @param {string} opts.nombreVendedor
 * @param {string} opts.idSurtido  _id del surtido (para marcar mail:"Si")
 * @param {string} opts.urlSurtidos
 * @param {string} opts.urlSendMailAtt
 */
export async function ofrecerMailNota({
  datosPDF,
  emailCliente,
  nombreCliente,
  emailVendedor,
  nombreVendedor,
  idSurtido,
  urlSurtidos,
  urlSendMailAtt,
}) {
  const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };

  // El cliente puede tener varios correos separados por coma
  const destinatarios = [];
  for (const mail of parseCorreos(emailCliente)) {
    destinatarios.push({ tipo: "cliente", email: mail });
  }
  for (const mailV of parseCorreos(emailVendedor)) {
    // No mandar dos veces si vendedor y cliente comparten correo
    if (!destinatarios.some((d) => d.email.toLowerCase() === mailV.toLowerCase())) {
      destinatarios.push({ tipo: "vendedor", email: mailV });
    }
  }

  if (destinatarios.length === 0) {
    Swal.fire({
      icon: "warning",
      title: "Nota no enviada",
      html: `Ni el cliente <b>${nombreCliente || ""}</b> ni el vendedor tienen correo válido registrado.<br/>La venta se guardó correctamente.`,
      timer: 3500,
      showConfirmButton: false,
    });
    return false;
  }

  let base64File;
  try {
    const doc = generaPDFNota(datosPDF);
    base64File = encode(doc.output("arraybuffer"));
  } catch (err) {
    console.log("[notaMail] error al generar el PDF", err);
    Swal.fire({
      icon: "error",
      title: "No se pudo generar el PDF",
      text: "La venta SÍ se guardó correctamente; sólo falló el envío del correo.",
    });
    return false;
  }

  const cuerpo = (esVendedor) => `<table border="0" cellpadding="0" cellspacing="0" width="100%">
    <th><img width="${process.env.REACT_APP_TAMANO_LOGO_MAIL}" align="center" src="${process.env.REACT_APP_LINK_SISTEMA}logoInstitucion.png" /></th>
    <tr align="center" style="font-size:30px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>${process.env.REACT_APP_NOMBRE_INSTITUCION}</tr>
    <tbody>
    ${
      esVendedor
        ? `<tr style="font-size:16px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px"><br>Hola ${nombreVendedor || ""},</tr>
           <tr style="font-size:16px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px"><br>Se generó la nota <b>${datosPDF?.numero || ""}</b> del cliente <b>${nombreCliente || ""}</b>. Adjunto encontrarás el documento.</tr>`
        : `<tr style="font-size:16px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px"><br>Estimado cliente ${nombreCliente || ""},</tr>
           <tr style="font-size:16px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px"><br>Adjunto encontrarás la nota <b>${datosPDF?.numero || ""}</b> correspondiente a la mercancía que se te envía.</tr>
           <tr style="font-size:16px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px"><br>Quedamos a tus órdenes para cualquier duda o aclaración.</tr>`
    }
    </tbody>
    <tr style="font-size:14px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px"><br>Favor de no responder este correo, para cualquier duda comunícate al ${process.env.REACT_APP_TELEFONO_INSTITUCION}</tr>
    </table>`;

  const enviados = [];
  const fallidos = [];

  for (const d of destinatarios) {
    try {
      await axios.post(
        urlSendMailAtt,
        {
          subject: `Nota ${datosPDF?.numero || ""} - ${process.env.REACT_APP_NOMBRE_INSTITUCION}`,
          email: d.email,
          fileName: `Nota-${datosPDF?.numero || ""}.pdf`,
          att: base64File,
          text: cuerpo(d.tipo === "vendedor"),
        },
        { headers }
      );
      enviados.push(d);
    } catch (err) {
      console.log(`[notaMail] falló envío a ${d.tipo} ${d.email}`, err);
      fallidos.push(d);
    }
  }

  // Deja registro de que la nota ya se mandó
  if (enviados.length && idSurtido && urlSurtidos) {
    try {
      await axios.patch(`${urlSurtidos}/${idSurtido}`, { mail: "Si" }, { headers });
    } catch (e) {
      console.log("[notaMail] no se pudo marcar mail:Si", e?.message);
    }
  }

  if (enviados.length && !fallidos.length) {
    Swal.fire({
      icon: "success",
      title: "Nota enviada",
      html: enviados.map((d) => `${d.tipo}: ${d.email}`).join("<br/>"),
      timer: 2500,
      showConfirmButton: false,
    });
  } else if (enviados.length && fallidos.length) {
    Swal.fire({
      icon: "warning",
      title: "Envío parcial",
      html:
        `Enviado a:<br/>${enviados.map((d) => `${d.tipo}: ${d.email}`).join("<br/>")}` +
        `<br/><br/>Falló:<br/>${fallidos.map((d) => `${d.tipo}: ${d.email}`).join("<br/>")}`,
    });
  } else {
    Swal.fire({
      icon: "error",
      title: "No se pudo enviar la nota",
      text: "La venta SÍ se guardó correctamente; sólo falló el envío del correo.",
    });
  }

  return enviados.length > 0;
}
